import React from "react";
import { Alert, Chip, Tooltip, Typography } from "@mui/material";

import { IDENTITY_WARNINGS } from "./identityCheck";

// How "whose document is this?" (identityCheck) reads to each audience: the
// applicant gets a plain warning under the file, staff get a chip per file.

const numbers = (ids) => (ids || []).join(", ");

// Under a stored file, for the applicant. Nothing unless something is off.
export function ApplicantIdentityWarning({ identity }) {
  if (!identity || !IDENTITY_WARNINGS.has(identity.status)) return null;
  const text = identity.status === "MISMATCH"
    ? `Este documento no parece ser tuyo${identity.otherIds?.length ? `: menciona el documento de identidad ${numbers(identity.otherIds)}` : ""}. Verifica que hayas subido el correcto; los documentos de otra persona no se tienen en cuenta.`
    : `Tu nombre aparece en el documento, pero con otro número de identificación${identity.otherIds?.length ? ` (${numbers(identity.otherIds)})` : ""}. Si es tu tarjeta de identidad anterior no hay problema; si no, revisa el documento.`;
  return (
    <Typography variant="caption" color={identity.status === "MISMATCH" ? "error" : "warning.main"} display="block" sx={{ mt: 0.25 }}>
      {text}
    </Typography>
  );
}

// Right after the AI read a certificate: who it says the document is about.
export function HolderWarning({ holder, check }) {
  if (!check || !IDENTITY_WARNINGS.has(check.status)) return null;
  const who = [holder?.holderName && `«${holder.holderName}»`, holder?.holderId && `documento ${holder.holderId}`]
    .filter(Boolean).join(", ");
  return (
    <Alert severity={check.status === "MISMATCH" ? "error" : "warning"} sx={{ mb: 1.5 }}>
      {check.status === "MISMATCH"
        ? `El documento parece estar a nombre de otra persona (${who}). Verifica que sea tuyo: los documentos de otra persona no se tienen en cuenta.`
        : `Tu nombre aparece en el documento, pero con otro número de identificación (${holder?.holderId}). Si es tu tarjeta de identidad anterior no hay problema; si no, revisa el documento.`}
    </Alert>
  );
}

const STAFF = {
  MATCH: { label: "Titular coincide", color: "success", variant: "outlined",
    tip: (i) => (i.idFound ? "El número de documento del aspirante aparece en el documento." : "El nombre del aspirante aparece en el documento.") },
  ID_DIFFERS: { label: "Otro Nº de documento", color: "warning", variant: "filled",
    tip: (i) => `El nombre del aspirante aparece, pero con otro número de identificación: ${numbers(i.otherIds)}. Puede ser una tarjeta de identidad anterior.` },
  MISMATCH: { label: "Titular no coincide", color: "error", variant: "filled",
    tip: (i) => `El documento menciona ${numbers(i.otherIds)}; no aparecen ni el número ni el nombre del aspirante.` },
  UNKNOWN: { label: "Titular sin verificar", color: "default", variant: "outlined",
    tip: () => "No se encontraron datos de identidad en el texto del documento (o no se pudo leer)." },
};

// One file's check, for the review table.
export function StaffIdentityChip({ identity }) {
  if (!identity || !STAFF[identity.status]) return null;
  const s = STAFF[identity.status];
  return (
    <Tooltip title={s.tip(identity)}>
      <Chip size="small" color={s.color} variant={s.variant} label={s.label} sx={{ mt: 0.5 }} />
    </Tooltip>
  );
}

// The applicant-level automatic review, for staff (list row, review header).
const SCREENING = {
  PASSED: { label: "Revisión automática: sin alertas", color: "success", variant: "outlined",
    tip: (s) => `${s.matched} documento(s) coinciden con el aspirante.` },
  FLAGGED: { label: "No pasó la revisión automática", color: "error", variant: "filled",
    tip: (s) => `${s.flagged} documento(s) no parecen ser del aspirante (otra cédula, u otro número con su nombre). Revísalos antes de validar.` },
  INCOMPLETE: { label: "Revisión automática incompleta", color: "warning", variant: "outlined",
    tip: (s) => `${s.unverified} documento(s) no se pudieron verificar (sin texto o sin datos de identidad). Revísalos manualmente.` },
};
export const SCREENING_LABELS = {
  PASSED: "Sin alertas",
  FLAGGED: "No pasó",
  INCOMPLETE: "Incompleta",
};

// `compact`: the short label, for a table cell.
export function ScreeningChip({ screening, compact = false }) {
  const s = screening && SCREENING[screening.status];
  if (!s) return null;
  return (
    <Tooltip title={s.tip(screening)}>
      <Chip size="small" color={s.color} variant={s.variant}
        label={compact ? SCREENING_LABELS[screening.status] : s.label} />
    </Tooltip>
  );
}
